import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { reactifiPageTitle as title } from '../metatags/title';
import { Toolbar } from './Toolbar';
import { PageBanner } from './PageBanner';
import { PageHeaderButton, PageHeaderButtonPropType } from './PageHeaderButton';
import { PageHeaderTabs } from './PageHeaderTabs';
import { FAIcon } from './fa-icon';
import { formatClassName } from '../utils';

/** renders the standard page header used in Foundry */
export class PageHeader extends Component {
  static propTypes = {
    /** configures the action buttons rendered in the header */
    actionButton: PageHeaderButtonPropType,
    /** renders a back button - true for default behavior
     *  or a function to be called instead */
    backButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    /** renders a PageBanner */
    banner: PropTypes.instanceOf(PageBanner),
    /** renders and child elements at the bottom of the header */
    children: PropTypes.any,
    /** renders a tabs right underneath the title */
    headerTabs: PropTypes.element,
    /** adds a class to the wrapper */
    headingClass: PropTypes.string,
    /** adds a zmdi icon in front of the page title */
    icon: PropTypes.string,
    /** overrides the value set for the document window title */
    overrideTitle: PropTypes.string,
    /** title displayed at the top of the page and sets the
     *  document window title */
    title: PropTypes.string,
    /** renders a toolbar */
    toolbar: PropTypes.instanceOf(Toolbar),
  };

  static defaultProps = {
    backButton: false,
    headingClass: 'row',
  };

  componentDidUpdate(prevProps) {
    const { title: pageTitle, overrideTitle } = prevProps;
    if (this.props.title !== pageTitle || this.props.overrideTitle !== overrideTitle) {
      this.setDocumentTitle(this.props.title, this.props.overrideTitle);
    }
  }

  /**
   * if overrideTitle is passed it will override HTML <title> tag.
   * if not passed it will take pageTitle as HTML <title> tag.
   */
  setDocumentTitle = (pageTitle, overrideTitle) => {
    if (overrideTitle) {
      title(overrideTitle, '%s');
    } else {
      title(pageTitle);
    }
  };

  onDefaultBack = e => {
    e.preventDefault();
    const { backButton } = this.props;
    if (backButton === true) {
      window.location.href = '#/';
    } else {
      backButton();
    }
  };

  get headerBarClasses() {
    const { banner, headerTabs, toolbar } = this.props;
    return formatClassName({
      toolbar,
      banner,
      'header-tabs': headerTabs,
    });
  }

  get headingClassName() {
    const { headingClass } = this.props;
    return formatClassName(['page-heading', headingClass, this.headerBarClasses]);
  }

  get headerBarClassName() {
    return formatClassName(['header-title-bar', this.headerBarClasses]);
  }

  renderBackButton = () => {
    const { backButton } = this.props;
    if (!backButton) {
      return null;
    }
    return (
      <a
        onClick={e => this.onDefaultBack(e)}
        className="header-back zmdi zmdi-hc-lg zmdi-arrow-back"
        href="#"
        aria-label="back"
        data-back-arrow={true}
      />
    );
  };

  renderHeaderIcon = () => {
    const { icon } = this.props;
    if (!icon) {
      return null;
    }
    return <FAIcon icon={icon} additionalClasses="header-icon" />;
  };

  renderHeaderButton = () => {
    const { actionButton } = this.props;
    if (!actionButton) {
      return null;
    }
    return <PageHeaderButton actionButton={actionButton} />;
  };

  renderHeaderBars = () => {
    const { banner, children, headerTabs, toolbar } = this.props;
    return (
      <div className="header-bars">
        {headerTabs && <PageHeaderTabs headerTabs={headerTabs} />}
        {toolbar}
        {banner}
        {children}
      </div>
    );
  };

  render() {
    return (
      <div className={this.headingClassName}>
        <div className={this.headerBarClassName}>
          {this.renderBackButton()}
          <h1 className="header-title">
            {this.renderHeaderIcon()}
            {this.props.title}
          </h1>
          {this.renderHeaderButton()}
        </div>
        {this.renderHeaderBars()}
      </div>
    );
  }
}
